import { ProductsDatasourceOption } from "../../../api/products";
import { AggregationFn, MetricConfig } from "../../../components/TrendOverTimeChart/TrendsOverTimeChart.types";
import { Match } from "../../../directus/collections";
import { MatchFilter } from "../../../libs/statsFilters";
import { forceArray } from "../../../libs/utils";
import { WorkerQueryTransferModes } from "../../../libs/workers/consts";
import { WorkerQueryOptions } from "../../../libs/workers/types";
import { webWorkerQuery } from "../../../libs/workers/webWorker";
import { CityEscapeData } from "../../../types/cityEscape";
import { GlobalStatsSelection } from "../../../types/stats";
import { generateDistinctColor } from "../../../utils/colors";
import { CityEscapeMetricsChartProps } from "./CityEscapeTrendsOverTimeChart.component";
import { AggregationKeyInput } from "./CityEscapeTrendsOverTimeChart.types";
import CityEscapeTrendsOverTimeChartWorker from "./CityEscapeTrendsOverTimeChart.worker.tsx?worker";

const AGGREGATION_FN_LABELS: Record<AggregationFn, string> = {
  count: "Totale",
  average: "Media",
  sum: "Somma",
};

const buildTotalLabel = (keyLabel: string, fn: AggregationFn): string => {
  const fnLabel = AGGREGATION_FN_LABELS[fn];
  return `${fnLabel} ${keyLabel}`;
};

export function getCityEscapesMetrics(
  {
    selection,
    matches,
    cityEscapes,
    aggregationKeyLabel,
    aggregationFn,
    comparatorAggregationKeyLabel,
    comparatorAggregationFn,
  }: {
    selection?: GlobalStatsSelection;
    filters?: MatchFilter;
    matches?: Match[];
    cityEscapes?: CityEscapeData[];
    aggregationKeyLabel?: string;
    aggregationFn?: AggregationFn;
    comparatorAggregationKeyLabel?: string;
    comparatorAggregationFn?: AggregationFn;
  },
  { defaultAggregationMode }: CityEscapeMetricsChartProps["opts"] = {}
) {
  if (!cityEscapes || !matches) return [];

  const variationLabel = aggregationKeyLabel && aggregationFn
    ? buildTotalLabel(aggregationKeyLabel, aggregationFn)
    : undefined;

  const comparatorVariationLabel = comparatorAggregationKeyLabel && comparatorAggregationFn
    ? buildTotalLabel(comparatorAggregationKeyLabel, comparatorAggregationFn)
    : undefined;

  const acc: MetricConfig[] = [
    {
      key: "all",
      label: "Totale",
      variationLabel,
      comparatorVariationLabel,
      // color: generateDistinctColor(),
      area: false,
      enabled: true,
      showMarker: false,
    },
  ];

  let i = 0;
  for (const cityEscape of cityEscapes) {
    i++;
    const hasMatches = matches.some?.((m) => m.city_escape === cityEscape.id);
    const isIncludedInSelection = forceArray(
      selection?.cityEscapeIds
    )?.includes(cityEscape.id);
    if (hasMatches && isIncludedInSelection) {
      acc.push({
        key: cityEscape.id,
        label: cityEscape.label,
        color: generateDistinctColor(Math.random()),
        area: defaultAggregationMode === "stack",
        enabled: false,
        showMarker: false,
        // stack: "total",
      });
    }
  }

  return acc;
}
export function getProductsEscapesMetrics(
  {
    selection,
    matches,
    products,
    aggregationKeyLabel,
    aggregationFn,
    comparatorAggregationKeyLabel,
    comparatorAggregationFn,
  }: {
    selection?: GlobalStatsSelection;
    filters?: MatchFilter;
    matches?: Match[];
    products?: ProductsDatasourceOption[];
    aggregationKeyLabel?: string;
    aggregationFn?: AggregationFn;
    comparatorAggregationKeyLabel?: string;
    comparatorAggregationFn?: AggregationFn;
  },
  { defaultAggregationMode }: CityEscapeMetricsChartProps["opts"] = {}
) {
  if (!products || !matches) return [];

  const variationLabel = aggregationKeyLabel && aggregationFn
    ? buildTotalLabel(aggregationKeyLabel, aggregationFn)
    : undefined;

  const comparatorVariationLabel = comparatorAggregationKeyLabel && comparatorAggregationFn
    ? buildTotalLabel(comparatorAggregationKeyLabel, comparatorAggregationFn)
    : undefined;

  const acc: MetricConfig[] = [
    {
      key: "all",
      label: "Totale",
      variationLabel,
      comparatorVariationLabel,
      // color: generateDistinctColor(),
      area: false,
      enabled: true,
      showMarker: false,
    },
  ];

  let i = 0;
  for (const product of products) {
    i++;
    const hasMatches = matches.some(
      (m) => String(m.product_id) === String(product.id)
    );
    const isIncludedInSelection = forceArray(selection?.productIds).includes(
      product.id
    );
    if (hasMatches && isIncludedInSelection) {
      acc.push({
        key: product.id,
        label: product.title,
        color: generateDistinctColor(Math.random()),
        area: defaultAggregationMode === "stack",
        enabled: false,
        showMarker: false,
        // stack: "total",
      });
    }
  }

  return acc;
}
export function getMetrics(
  {
    dataType,
    selection,
    cityEscapes,
    products,
    matches,
    aggregationKeyLabel,
    aggregationFn,
    comparatorAggregationKeyLabel,
    comparatorAggregationFn,
  }: {
    dataType: "cityEscape" | "product";
    selection?: GlobalStatsSelection;
    filters?: MatchFilter;
    cityEscapes?: CityEscapeData[];
    products?: ProductsDatasourceOption[];
    matches?: Match[];
    aggregationKeyLabel?: string;
    aggregationFn?: AggregationFn;
    comparatorAggregationKeyLabel?: string;
    comparatorAggregationFn?: AggregationFn;
  },
  { defaultAggregationMode }: CityEscapeMetricsChartProps["opts"] = {}
) {
  if (dataType === "cityEscape") {
    return getCityEscapesMetrics(
      {
        selection,
        cityEscapes,
        matches,
        aggregationKeyLabel,
        aggregationFn,
        comparatorAggregationKeyLabel,
        comparatorAggregationFn,
      },
      { defaultAggregationMode }
    );
  } else {
    return getProductsEscapesMetrics(
      {
        selection,
        products,
        matches,
        aggregationKeyLabel,
        aggregationFn,
        comparatorAggregationKeyLabel,
        comparatorAggregationFn,
      },
      { defaultAggregationMode }
    );
  }
}

export function getEntries({
  selection,
  dataType = "cityEscape",
  aggregationKey,
  aggregationKeys,
  yAxisId,
  filters,
  opts,
}: {
  selection?: GlobalStatsSelection;
  dataType?: "cityEscape" | "product";
  yAxisId?: string;
  aggregationKey?: string;
  aggregationKeys?: AggregationKeyInput[];
  filters?: MatchFilter;
  opts?: WorkerQueryOptions;
} = {}) {
  const worker = new CityEscapeTrendsOverTimeChartWorker();
  return webWorkerQuery(worker, {
    type: "GET_ENTRIES",
    data: {
      cityEscapeIds: selection?.cityEscapeIds,
      productIds: selection?.productIds,
      aggregationKey,
      aggregationKeys,
      yAxisId,
      dataType,
      filters,
      opts: {
        traceKey:
          opts?.traceKey ??
          `CityEscapeTrendsOverTimeChartEntries-${Date.now()}`,
        ...opts,
        response: {
          transferMode: WorkerQueryTransferModes.SHARED_ARRAY_BUFFER,
          ...(opts?.response || {}),
        },
      },
    },
  }).finally(() => worker.terminate());
}
